import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useState } from "react";
import { useTable, usePagination, useSortBy, useFilters, useExpanded } from "react-table";

export default function TableComponentPaginationClient({
  columns,
  data = [], // Default a un array vuoto se `data` non è passato
  renderRowSubComponent,
  pageSizeParent = 5, // Default a 5 se `pageSizeParent` non è passato
  type,
  isDisabledPagination,
}: any) {

  const [pageSize, setPageSize] = useState(pageSizeParent); // Usa `pageSizeParent` con default 5
  const [rowSelected, setRowSelected] = useState<string | null>('');

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    visibleColumns,
    setPageSize: setTablePageSize,
    state: { pageIndex, sortBy },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: pageSize }, // Usa `pageSize` come valore iniziale
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  // Funzione per la navigazione delle pagine
  const getVisiblePages = (page: number, total: number) => {
    const arrPage = [];
    if (total <= 5) {
      for (let i = 1; i <= total; i++) {
        arrPage.push(i);
      }
      return arrPage;
    } else {
      if (page > 4 && page + 2 < total) {
        return [1, page - 1, page, page + 1, total];
      } else if (page > 4 && page + 2 >= total) {
        return [1, total - 3, total - 2, total - 1, total];
      } else {
        return [1, 2, 3, 4, 5, total];
      }
    }
  };

  const [visiblePages, setVisiblePages] = useState<any[]>([]);

  useEffect(() => {
    const arrOption = getVisiblePages(pageIndex + 1, pageOptions.length);
    setVisiblePages(arrOption);
  }, [pageIndex, pageOptions.length]);

  const handleSort = (column: any) => {
    let direction: string | null = null;

    if (column.customSort) {
      if (column.id !== sortBy[0]?.id) {
        direction = 'ASC';
      } else if (sortBy[0]?.desc) {
        direction = 'ASC';
      } else {
        direction = 'DESC';
      }
      // ordinamento locale (client-side)
      column.toggleSortBy();
    }
  };

  return (
    <div className="mt-2 flex flex-col">
      <div className="col-span-12 overflow-x-auto">
        <div className="overflow-auto lg:overflow-visible">
          <table
            {...getTableProps()}
            className="min-w-full table table-auto px-2 relative border-separate space-y-6 text-sm"
          >
            <thead className="bg-transparent text-[#1e293b]">
              {headerGroups.map((headerGroup: any) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className="group p-3 text-left text-xs uppercase"
                      onClick={() => handleSort(column)}
                    >
                      <div className={`my-2 inline-flex group ${column.customSort ? 'cursor-pointer' : ''}`}>
                        <span>{column.render('Header')}</span>
                        {column?.customSort && !sortBy.length && (
                          <span><ChevronUpIcon className="w-3 h-3 ml-4 mt-1 group-hover:opacity-100 opacity-0" /></span>
                        )}
                        {column.customSort && sortBy[0]?.id === column.id && (
                          <span>
                            {sortBy[0]?.desc ? (
                              <ChevronDownIcon className="w-3 h-3 ml-4 mt-1 inline-block" />
                            ) : (
                              <ChevronUpIcon className="w-3 h-3 ml-4 mt-1 inline-block" />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {page.map((row: any) => {
                prepareRow(row);
                return (
                  <Fragment key={row.getRowProps().key}>
                    <tr
                      className={`shadow-md h-14 text-black! ${
                        row.original.id === rowSelected ? "bg-green-200 " : "bg-white"
                      }`}
                      onClick={() => setRowSelected(row.original.id === rowSelected ? null : row.original.id)}
                    >
                      {row.cells.map((cell: any, index: number) => (
                        <td
                          {...cell.getCellProps({
                            style: {
                              minWidth: cell.column.minWidth,
                              maxWidth: cell.column.maxWidth,
                              width: cell.column.width,
                            },
                          })}
                          className={`${
                            type === "userList" ? (row.original.active ? "" : "last:opacity-100") : ""
                          } p-2 whitespace-nowrap text-base ${
                            row.cells.length - 1 === index && row.original.id === rowSelected && "border-r-4 border-r-blue-600"
                          }`}
                        >
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                    {row.isExpanded && (
                      <tr className="container">
                        <td colSpan={visibleColumns.length}>{renderRowSubComponent(row)}</td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINATION */}
      {!isDisabledPagination && (
        <div className="py-4 text-right flex space-x-12 justify-end items-center">
          {/* SELECT ITEMS PER PAGE */}
          <nav className="space-x-4">
            <span className="text-sm text-skin-placeholder">Totale: <strong>{data.length}</strong></span>
            <span className="text-sm text-skin-placeholder">Elementi per pagina</span>
            <select
              className="border-0 border-b-2 bg-transparent outline-none leading-8 transition-colors duration-200 ease-in-out"
              value={pageSize}
              onChange={(e) => {
                const newPageSize = Number(e.target.value);
                setPageSize(newPageSize);
                setTablePageSize(newPageSize); // Aggiorna il pageSize nella tabella
                gotoPage(0); // Reset to first page on page size change
              }}
            >
              {[5, 10, 25, 50].map((size) => ( // Aggiunto 5 come opzione
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </nav>

          {/* PAGINATION BUTTONS */}
          <nav className="relative z-0 inline-flex" aria-label="Pagination">
            <button
              className="relative inline-flex items-center justify-center pr-2"
              onClick={() => gotoPage(pageIndex - 1)}
              disabled={pageIndex === 0}
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>

            {visiblePages.map((el: number) => (
              <button
                key={`page-${el}`}
                onClick={() => gotoPage(el - 1)}
                className={`mx-1 inline-flex justify-center items-center h-6 w-9 text-xs ${el === pageIndex + 1 ? "bg-[#064e3b] text-white" : "bg-gray-100"}`}
              >
                {el}
              </button>
            ))}

            <button
              className="relative inline-flex items-center justify-center pl-2"
              onClick={() => gotoPage(pageIndex + 1)}
              disabled={pageIndex === pageOptions.length - 1}
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </nav>
        </div>
      )}
    </div>
  );
}